import { useEffect } from "react";
import {
  PlatformResourceName,
  PlatformResourceStatus,
  useAppStore,
} from "../../../providers/AppStore";

/**
 * Declare the client dataset a page needs, and get its loading status back.
 *
 * Client data used to be front-loaded by `loadClientData` before the workspace
 * would render at all, so opening any page paid for the slowest request
 * (`GetReportv2`, ~8s) even when that page never read it. Pages now ask for what
 * they use and render around the wait.
 *
 * `subjectId` and `role` are passed explicitly rather than read from
 * `useSubject()` so this also works outside the workspace shell — the
 * consultation screen has its own route and no SubjectProvider above it.
 *
 * Deduped and cached per client id by the store, so several components on one
 * page asking for the same resource cost one request, and navigating away and
 * back costs none.
 */
export const usePlatformResource = (
  name: PlatformResourceName,
  subjectId: string | null | undefined,
  role: number | null | undefined
): PlatformResourceStatus => {
  const { ASEnsureResource, ASResourceStatus } = useAppStore();

  useEffect(() => {
    if (!subjectId || role == null) return;
    ASEnsureResource(name, subjectId, role);
  }, [name, subjectId, role, ASEnsureResource]);

  return ASResourceStatus(name, subjectId);
};

/**
 * True while the resource has not arrived yet — the common case for gating a
 * skeleton. `idle` counts as loading: the effect above has not run yet, so the
 * data is equally absent and rendering the real component would read undefined.
 */
export const isResourcePending = (status: PlatformResourceStatus): boolean =>
  status === "idle" || status === "loading";
